import api from './api';
import { reportNotificationOpened } from './push-notification.service';
import {
  IN_CALL_QUERY,
  isInLiveCall,
  NOTIFICATION_TAP_MESSAGE,
  showInCallTapNotice,
} from './notification-tap-in-call';
import { isCurrentSession, issueRead, onSessionChange } from '../stores/session-read';

/**
 * Web Push registration helpers (PR13).
 *
 * Two-step flow:
 *   1. Register the service worker (sw.js at site root)
 *   2. Subscribe to the browser's push service via VAPID public key
 *      → POST the resulting subscription to the backend
 *
 * isWebPushSupported() returns false on Safari < iOS 16.4 and any
 * desktop browser without Push API. Callers should check before
 * surfacing the "Enable" button in the preferences UI.
 *
 * VAPID public key comes from VITE_VAPID_PUBLIC_KEY at build time.
 * Backend signs each delivery with the matching private key.
 */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY
  );
}

export async function getCurrentPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.warn('[WebPush] SW registration failed:', err);
    return null;
  }
}

export async function subscribe(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!isWebPushSupported()) {
    return { ok: false, reason: 'not_supported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: `permission_${permission}` };
  }

  if (!(await registerServiceWorker())) return { ok: false, reason: 'sw_registration_failed' };

  let current: CurrentSubscription;
  try {
    // register() resolves while a first-time worker is still installing, and
    // PushManager.subscribe() on that registration fails "no active Service
    // Worker". ready resolves with the registration once a worker is active.
    const reg = await navigator.serviceWorker.ready;
    current = await currentKeySubscription(reg);
  } catch (err) {
    // The push service refused (offline, blocked by policy, ...): a DOMException.
    console.warn('[WebPush] subscribe failed:', err);
    return { ok: false, reason: 'push_service_refused' };
  }

  try {
    await saveSubscription(current);
  } catch {
    return { ok: false, reason: 'save_failed' };
  }

  return { ok: true };
}

/**
 * This browser's subscription, made with the CURRENT VAPID key. A subscription
 * made under an older key (the key was rotated, or this browser was last used
 * against another environment) is refused by the push service with 403 for
 * every send, so it is replaced rather than re-posted.
 */
type CurrentSubscription = {
  subscription: PushSubscription;
  /** The endpoint this subscription replaced, whose server row is now dead. */
  replaces?: string;
};

async function currentKeySubscription(
  reg: ServiceWorkerRegistration,
): Promise<CurrentSubscription> {
  const key = urlBase64ToUint8Array(VAPID_PUBLIC_KEY!);
  const existing = await reg.pushManager.getSubscription();
  if (existing && sameKey(existing.options.applicationServerKey, key)) {
    return { subscription: existing };
  }
  const replaces = existing?.endpoint;
  if (existing) await existing.unsubscribe();
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast to BufferSource — modern lib.dom.d.ts expects BufferSource
    // and Uint8Array satisfies that contract at runtime, just not in
    // the narrowest TS overload.
    applicationServerKey: key as BufferSource,
  });
  return replaces ? { subscription, replaces } : { subscription };
}

// Persist the subscription server-side so we can target it. The server
// upserts by endpoint, so this also moves a browser to whoever is signed in.
// A replaced endpoint rides along so the server deletes its row in the same
// request: left behind, it 403s every send and counts as another browser.
async function saveSubscription({ subscription, replaces }: CurrentSubscription): Promise<void> {
  await api.post('/profile/notifications/web-push', {
    ...subscription.toJSON(),
    ...(replaces ? { replaces_endpoint: replaces } : {}),
  });
}

function sameKey(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false;
  const bytes = new Uint8Array(a);
  return bytes.every((v, i) => v === b[i]);
}

/** Whether THIS browser holds a push subscription (the server lists every browser). */
export async function isThisBrowserSubscribed(): Promise<boolean> {
  if (!isWebPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  return !!(reg && (await reg.pushManager.getSubscription()));
}

/**
 * At sign-in on the web: if this browser already holds a subscription (the
 * person said yes before, and has not pressed Disable since: that
 * unsubscribes), re-send it for whoever is signed in now. It covers a row the
 * server dropped after a 404/410, a browser that changed accounts, and a key
 * rotation. Never prompts: a browser without a subscription is left alone.
 */
export async function resyncWebPush(isCurrent: () => boolean): Promise<void> {
  if (!isWebPushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !(await reg.pushManager.getSubscription())) return;
    const current = await currentKeySubscription(reg);
    if (!isCurrent()) return;
    await saveSubscription(current);
  } catch (err) {
    console.warn('[WebPush] resync failed:', err);
  }
}

export async function unsubscribe(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api.delete('/profile/notifications/web-push', { data: { endpoint } });
  } catch {
    // Even if the server-side delete fails, the local subscription is
    // gone — server cleanup will happen on the next 410 from the push
    // service.
  }
}

/** How long a sign-out waits for this browser's DELETE (the queue leave's bound). */
export const WEB_PUSH_END_TIMEOUT_MS = 4000;

/**
 * A session is ending on this browser (backlog c-027): a browser holds ONE
 * push subscription, so left alone it keeps showing the previous account's
 * ring and claim pushes to whoever uses the browser next. This deletes the
 * server row under the ENDING session's own token (pinned: the store may
 * already hold the next token, or none), then unsubscribes the browser.
 *
 * The browser is unsubscribed whatever the DELETE answered (a 401 from an
 * expired token, a 403 for a row another account re-owned, a timeout): the
 * push service then refuses the endpoint, and the server drops the row at its
 * next send's 404/410. It is skipped only when `stillEnding` says a new
 * session began meanwhile, as that account's sign-in resync is re-sending
 * this very subscription. Bounded by WEB_PUSH_END_TIMEOUT_MS; never throws.
 */
export async function endWebPushForSession(
  sessionToken: string,
  stillEnding: () => boolean,
): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, WEB_PUSH_END_TIMEOUT_MS);
    });
    const deleted = api
      .delete('/profile/notifications/web-push', {
        data: { endpoint: sub.endpoint },
        sessionToken,
        timeout: WEB_PUSH_END_TIMEOUT_MS,
      })
      .catch((err: unknown) => console.warn('[WebPush] delete on session end failed:', err));
    await Promise.race([deleted, timedOut]);
    clearTimeout(timer);

    if (!stillEnding()) return;
    await sub.unsubscribe();
  } catch (err) {
    console.warn('[WebPush] unsubscribe on session end failed:', err);
  }
}

// Every session that ends without the sign-out button (a 401, a ban teardown)
// takes this browser's subscription with it too. Sign-out has already done
// this before /logout revoked its token, so here it finds nothing left. A
// switch A→B keeps the subscription: B's resync re-owns the endpoint.
onSessionChange((next, previous) => {
  if (!previous || next !== null) return;
  const ended = issueRead(null);
  void endWebPushForSession(previous, () => isCurrentSession(ended));
});

// VAPID public key is a URL-safe base64 string; PushManager.subscribe
// wants a Uint8Array. Standard conversion helper.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** What to tell the person when subscribe() came back { ok: false }. */
export function subscribeFailureText(reason: string): string {
  switch (reason) {
    case 'permission_denied':
      return "Notifications are blocked for this site. Allow them in your browser's site settings, then try again.";
    case 'permission_default':
      return 'Notifications were not allowed. Try again and choose Allow.';
    case 'not_supported':
      return "This browser can't receive notifications from EmpathEase.";
    default:
      return "Couldn't turn on notifications in this browser. Try again in a moment.";
  }
}

/** What sw.js posts to the page a notification click opened. */
export const NOTIFICATION_CLICK_MESSAGE = 'empathease:notification-click';

/**
 * Count browser notification clicks (open rate covered only the phone): the
 * worker holds no sign-in, so it posts the clicked push's type to the page it
 * focused or opened, and the page reports it as the signed-in user. Called
 * once at boot (main.tsx), before the document finishes loading, so a page
 * the click opened gets the message (the client message queue holds it).
 * Also the worker's other two click messages: "are you in a call?" before it
 * navigates a tab, and the tap it did not navigate for because the answer was
 * yes (notification-tap-in-call.ts).
 */
export function listenForNotificationClicks(): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const container = navigator.serviceWorker;
  const onMessage = (event: MessageEvent) => {
    const message: unknown = event.data;
    if (typeof message !== 'object' || message === null) return;
    const { kind, type, title, route } = message as {
      kind?: unknown;
      type?: unknown;
      title?: unknown;
      route?: unknown;
    };
    // Before a click navigates this tab, the worker asks whether it is in a
    // live call (notification-tap-in-call.ts). The answer goes back on the
    // port it sent; a refusal to answer reads as "not in a call" there.
    if (kind === IN_CALL_QUERY) {
      const port = event.ports?.[0];
      if (!port) return;
      void isInLiveCall().then(
        (inCall) => port.postMessage({ inCall }),
        () => port.postMessage({ inCall: false }),
      );
      return;
    }
    // A click the worker did not navigate this tab for, because it is in a call.
    if (kind === NOTIFICATION_TAP_MESSAGE) {
      if (typeof route !== 'string' || route === '') return;
      showInCallTapNotice(typeof title === 'string' ? title : '', route);
      return;
    }
    if (kind !== NOTIFICATION_CLICK_MESSAGE || typeof type !== 'string' || type === '') return;
    void reportNotificationOpened(type, 'web');
  };
  container.addEventListener('message', onMessage);
  container.startMessages?.();
  return () => container.removeEventListener('message', onMessage);
}
